import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Star,
  Calendar,
  Brain,
  Target,
  CheckCircle,
  Loader2,
} from "lucide-react";

interface AdvancedAnalyticsData {
  revenueData: Array<{
    month: string;
    revenue: number;
    booking_count: number;
    growth_percentage: number;
  }>;
  customerSegments: Array<{
    segment: string;
    customer_count: number;
    total_revenue: number;
    avg_order_value: number;
  }>;
  servicePerformance: Array<{
    service_name: string;
    booking_count: number;
    total_revenue: number;
    avg_rating: number;
    completion_rate: number;
  }>;
  customerBehavior: Array<{
    metric: string;
    value: number;
    target: number;
    trend: 'up' | 'down';
  }>;
  geographicData: Array<{
    city: string;
    booking_count: number;
    total_revenue: number;
    growth_percentage: number;
  }>;
  timeAnalysis: Array<{
    time_period: string;
    booking_count: number;
    total_revenue: number;
    avg_rating: number;
  }>;
}

interface CompanyAdvancedAnalyticsProps {
  companyId?: string;
}

export default function CompanyAdvancedAnalytics({ companyId }: CompanyAdvancedAnalyticsProps) {
  const { t } = useI18n();
  const [analyticsData, setAnalyticsData] = useState<AdvancedAnalyticsData>({
    revenueData: [],
    customerSegments: [],
    servicePerformance: [],
    customerBehavior: [],
    geographicData: [],
    timeAnalysis: [],
  });
  
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('6');
  const [activeTab, setActiveTab] = useState('revenue');

  const fetchAnalyticsData = async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`/api/analytics/company?companyId=${companyId}&type=all&months=${timeRange}`);
      const result = await response.json();
      
      if (result.data) {
        // Transform the data to match our interface
        const transformedData: AdvancedAnalyticsData = {
          revenueData: result.data.monthlyRevenue || [],
          customerSegments: result.data.customerSegments || [],
          servicePerformance: result.data.mostRequestedServices?.map((service: any) => ({
            service_name: service.service_name,
            booking_count: service.booking_count,
            total_revenue: service.total_revenue,
            avg_rating: service.avg_rating,
            completion_rate: typeof service.completion_rate === 'number' ? service.completion_rate : 0
          })) || [],
          customerBehavior: [
            {
              metric: "Repeat Customers",
              value: result.data.customerRetention?.retention_rate || 0,
              target: 75,
              trend: "up"
            },
            {
              metric: "Average Order Value",
              value: result.data.serviceMetrics?.avg_booking_value || 0,
              target: 300,
              trend: "up"
            },
            {
              metric: "Customer Lifetime Value",
              value: (result.data.serviceMetrics?.avg_booking_value || 0) * 3, // Estimate
              target: 1000,
              trend: "up"
            },
            {
              metric: "Churn Rate",
              value: result.data.customerRetention?.churn_rate || 0,
              target: 8,
              trend: "down"
            },
            {
              metric: "Response Time",
              value: result.data.serviceMetrics?.response_time_hours || 0,
              target: 2.0,
              trend: "down"
            },
            {
              metric: "Customer Satisfaction",
              value: result.data.serviceMetrics?.avg_rating || 0,
              target: 4.8,
              trend: "up"
            }
          ],
          geographicData: result.data.geographicData || [],
          timeAnalysis: result.data.timeAnalysis || []
        };
        
        setAnalyticsData(transformedData);
      }
    } catch (error) {
      console.error('Failed to fetch advanced analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [companyId, timeRange]);

  const COLORS = ['#60a5fa', '#34d399', '#fbbf24', '#a78bfa', '#f472b6', '#22d3ee'];
  const chartGrid = '#334155';
  const chartAxis = '#94a3b8';
  const tooltipStyle = {
    backgroundColor: 'rgba(2, 8, 23, 0.96)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    borderRadius: '18px',
    boxShadow: '0 24px 70px -36px rgba(2, 8, 23, 0.92)',
  };

  if (loading) {
    return (
      <div className="app-dark-panel flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-200" />
        <span className="ml-2 text-slate-200">Loading advanced analytics...</span>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="app-dark-panel flex items-center justify-center h-64">
        <div className="text-center">
          <Brain className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">Advanced Analytics</h3>
          <p className="text-slate-300">
            Company ID is required to load advanced analytics data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="app-dark-panel border border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-white">
                <Brain className="h-5 w-5 text-sky-300" />
                Advanced Analytics
              </CardTitle>
              <p className="text-sm text-slate-300">
                Deep insights into your business performance and customer behavior
              </p>
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32 border-white/10 bg-white/5 text-slate-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 Month</SelectItem>
                <SelectItem value="3">3 Months</SelectItem>
                <SelectItem value="6">6 Months</SelectItem>
                <SelectItem value="12">12 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-6 bg-white/6 border border-white/10">
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
              <TabsTrigger value="customers">Customers</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="behavior">Behavior</TabsTrigger>
              <TabsTrigger value="geographic">Geographic</TabsTrigger>
              <TabsTrigger value="time">Time Analysis</TabsTrigger>
            </TabsList>

            <TabsContent value="revenue" className="space-y-6">
              <Card className="app-dark-metric border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Revenue Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={analyticsData.revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                      <XAxis dataKey="month" stroke={chartAxis} />
                      <YAxis yAxisId="left" stroke={chartAxis} />
                      <YAxis yAxisId="right" orientation="right" stroke={chartAxis} />
                      <Tooltip 
                        contentStyle={tooltipStyle}
                        labelStyle={{ color: '#e2e8f0' }}
                        itemStyle={{ color: '#f8fafc' }}
                        formatter={(value, name) => [
                          name === 'revenue' ? `€${value.toLocaleString()}` : value,
                          name === 'revenue' ? 'Revenue' : name === 'booking_count' ? 'Bookings' : 'Growth %'
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="revenue" fill="#8884d8" name="Revenue" />
                      <Line yAxisId="right" type="monotone" dataKey="growth_percentage" stroke="#ff7300" name="Growth %" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="customers" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="app-dark-metric border border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Customer Segments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={analyticsData.customerSegments}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ segment, percent }) => `${segment} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="customer_count"
                        >
                          {analyticsData.customerSegments.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, 'Customers']} contentStyle={tooltipStyle} labelStyle={{ color: '#e2e8f0' }} itemStyle={{ color: '#f8fafc' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="app-dark-metric border border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Segment Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analyticsData.customerSegments.map((segment, index) => (
                        <div key={segment.segment} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-white">{segment.segment}</span>
                            <span className="text-sm text-slate-300">
                              {segment.customer_count} customers
                            </span>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs text-slate-300">
                              <span>Revenue: €{segment.total_revenue.toLocaleString()}</span>
                              <span>AOV: €{segment.avg_order_value.toFixed(2)}</span>
                            </div>
                            <Progress 
                              value={(segment.total_revenue / Math.max(...analyticsData.customerSegments.map(s => s.total_revenue))) * 100} 
                              className="h-2" 
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="services" className="space-y-6">
              <Card className="app-dark-metric border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Service Performance Matrix</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={analyticsData.servicePerformance}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                      <XAxis dataKey="service_name" angle={-45} textAnchor="end" height={100} stroke={chartAxis} />
                      <YAxis yAxisId="left" stroke={chartAxis} />
                      <YAxis yAxisId="right" orientation="right" stroke={chartAxis} />
                      <Tooltip 
                        contentStyle={tooltipStyle}
                        labelStyle={{ color: '#e2e8f0' }}
                        itemStyle={{ color: '#f8fafc' }}
                        formatter={(value, name) => [
                          name === 'total_revenue' ? `€${value.toLocaleString()}` : value,
                          name === 'total_revenue' ? 'Revenue' : name === 'booking_count' ? 'Bookings' : 'Rating'
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="booking_count" fill="#8884d8" name="Bookings" />
                      <Bar yAxisId="left" dataKey="total_revenue" fill="#82ca9d" name="Revenue" />
                      <Line yAxisId="right" type="monotone" dataKey="avg_rating" stroke="#ff7300" name="Rating" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="behavior" className="space-y-6">
              <Card className="app-dark-metric border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Customer Behavior Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analyticsData.customerBehavior.map((metric, index) => (
                      <Card key={metric.metric} className="app-dark-metric border border-white/10 p-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-white">{metric.metric}</span>
                            <div className="flex items-center gap-1">
                              {metric.trend === 'up' ? (
                                <TrendingUp className="h-4 w-4 text-green-500" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-white">
                            {metric.metric.includes('Rate') || metric.metric.includes('Satisfaction') 
                              ? `${metric.value}${metric.metric.includes('Rate') ? '%' : ''}`
                              : metric.metric.includes('Time')
                              ? `${metric.value}h`
                              : `€${metric.value.toLocaleString()}`
                            }
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs text-slate-300">
                              <span>Target: {metric.metric.includes('Rate') || metric.metric.includes('Satisfaction') 
                                ? `${metric.target}${metric.metric.includes('Rate') ? '%' : ''}`
                                : metric.metric.includes('Time')
                                ? `${metric.target}h`
                                : `€${metric.target.toLocaleString()}`
                              }</span>
                            </div>
                            <Progress 
                              value={Math.min((metric.value / metric.target) * 100, 100)} 
                              className="h-2" 
                            />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="geographic" className="space-y-6">
              <Card className="app-dark-metric border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Geographic Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={analyticsData.geographicData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                      <XAxis dataKey="city" stroke={chartAxis} />
                      <YAxis yAxisId="left" stroke={chartAxis} />
                      <YAxis yAxisId="right" orientation="right" stroke={chartAxis} />
                      <Tooltip 
                        contentStyle={tooltipStyle}
                        labelStyle={{ color: '#e2e8f0' }}
                        itemStyle={{ color: '#f8fafc' }}
                        formatter={(value, name) => [
                          name === 'total_revenue' ? `€${value.toLocaleString()}` : value,
                          name === 'total_revenue' ? 'Revenue' : name === 'booking_count' ? 'Bookings' : 'Growth %'
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="booking_count" fill="#8884d8" name="Bookings" />
                      <Bar yAxisId="left" dataKey="total_revenue" fill="#82ca9d" name="Revenue" />
                      <Line yAxisId="right" type="monotone" dataKey="growth_percentage" stroke="#ff7300" name="Growth %" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="time" className="space-y-6">
              <Card className="app-dark-metric border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Time-based Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={analyticsData.timeAnalysis}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                      <XAxis dataKey="time_period" stroke={chartAxis} />
                      <YAxis yAxisId="left" stroke={chartAxis} />
                      <YAxis yAxisId="right" orientation="right" stroke={chartAxis} />
                      <Tooltip 
                        contentStyle={tooltipStyle}
                        labelStyle={{ color: '#e2e8f0' }}
                        itemStyle={{ color: '#f8fafc' }}
                        formatter={(value, name) => [
                          name === 'total_revenue' ? `€${value.toLocaleString()}` : value,
                          name === 'total_revenue' ? 'Revenue' : name === 'booking_count' ? 'Bookings' : 'Rating'
                        ]}
                      />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="booking_count" stackId="1" stroke="#8884d8" fill="#8884d8" name="Bookings" />
                      <Area yAxisId="left" type="monotone" dataKey="total_revenue" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Revenue" />
                      <Line yAxisId="right" type="monotone" dataKey="avg_rating" stroke="#ff7300" name="Rating" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}